import type {
  IFacebookData,
  IInstagramData,
  ILinkedInData,
  IntegrationTypes,
  IUserIntegration,
  IYoutubeData,
} from "@/types/account";
import type { IContentBase, IContentSelectOption } from "@/types/content";
import type {
  ScheduledSocialPostData,
  WordpressPostData,
  YouTubeScheduledPostData,
  IScheduledContent,
  ScheduledContentType,
  ContentExportType,
} from "@/types/contentCalendar";
import type {
  CombinedContentTaskResult,
  ResearchWritePostResult,
  SocialPostResult,
  WriteInstagramPostResult,
  WriteLinkedInPostResult,
  WritePostResult,
  WriteTwitterPostResult,
} from "@/types/task";
import { addMinutes } from "date-fns";
import { get } from "lodash";

export type EditableContentType = "plain-text" | "markdown" | "array-list";

export type ContentIntegrationType = Extract<
  IntegrationTypes,
  "facebook" | "twitter" | "instagram" | "linkedin" | "wordpress" | "tiktok" | "youtube"
>;

export const editableContentTypeOptions: EditableContentType[] = [
  "plain-text",
  "markdown",
  "array-list",
];

const postTypeMap = {
  facebook: "message",
  instagram: "single",
  twitter: "text",
  linkedin: "text",
  tiktok: "video",
} as const;

export function getNewScheduledSocialPostData(
  platform?: Exclude<ContentIntegrationType, "wordpress" | "youtube">
): ScheduledSocialPostData {
  return {
    postType: postTypeMap[platform || "facebook"],
    preferredText: "",
    contentId: "",
    integrationIds: [],
    organizationsIds: [],
  };
}

export function getInstagramPostType(
  images?: string[],
  videos?: string[]
): "single" | "carousel" {
  const totalMedia = (images?.length ?? 0) + (videos?.length ?? 0);
  return totalMedia > 1 ? "carousel" : "single";
}

export function getTikTokPostType(
  videos?: string[]
  // images?: string[]
): "video" | "photo" {
  if (videos?.length) return "video";
  // Photo support disabled for now — TikTok only supports video posting
  // if (images?.length) return "photo";
  return "video";
}

export function getNewWordpressPostData(): WordpressPostData {
  return {
    contentId: "",
    preferredText: "",
    integrationIds: [],
    organizationsIds: [],
  };
}

export function appendShortsHashtag(text = ""): string {
  if (/#shorts\b/i.test(text)) return text;
  const trimmed = text.trim();
  return trimmed ? `${trimmed}\n\n#Shorts` : "#Shorts";
}

export function getNewYouTubeScheduledPostData(): YouTubeScheduledPostData {
  return {
    postType: "video",
    contentId: "",
    integrationIds: [],
    organizationsIds: [],
    privacyStatus: "public",
    categoryId: "22",
    madeForKids: false,
  };
}

export function getNewScheduledContentData(
  type: ReturnType<typeof getIntegrationIdContentType>
) {
  if (!type) return null;
  switch (type) {
    case "wordpress":
      return getNewWordpressPostData();
    case "youtube":
      return getNewYouTubeScheduledPostData();
    default:
      return getNewScheduledSocialPostData(type);
  }
}

export function getNewContentSchedulePayload(): IScheduledContent {
  return {
    taskType: "" as any,
    data: getNewScheduledSocialPostData(),
    scheduleTime: {
      type: "cron",
      value: {
        interval: "once",
        startDateTime: addMinutes(new Date(), 30).toISOString(),
      },
    },
  };
}

export const contentAutoPostTypeArr: ScheduledContentType[] = [
  "social-media/facebook/post-to-facebook",
  "social-media/twitter/post-to-twitter",
  "social-media/instagram/post-to-instagram",
  "social-media/linkedin/post-to-linkedin",
  "social-media/tiktok/post-to-tiktok",
  "social-media/wordpress/post-to-wordpress",
];

export const contentExportPlatformArr: ContentExportType[] = [
  "default",
  "hootsuite",
  "sprout",
];

export function getContentExportPlatformIcon(platform: ContentExportType) {
  switch (platform) {
    case "hootsuite":
      return defineAsyncComponent(() => import("@/components/icons/HootsuiteIcon.vue"));
    case "sprout":
      return defineAsyncComponent(
        () => import("@/components/icons/SproutSocialIcon.vue")
      );
    case "default":
      return defineAsyncComponent(
        () => import("@/components/icons/ExcelDocumentIcon.vue")
      );
    default:
      return null;
  }
}

export function getTaskContentScheduleQuery(
  contentId: string,
  taskType: ScheduledContentType,
  isQuickTask?: boolean
) {
  const contentType = getIntegrationIdContentType(taskType);
  if (!contentType) return null;
  return {
    scheduledContentId: contentId,
    scheduledContentType: contentType,
    isQuickTask: isQuickTask ? "1" : "0",
  };
}

export function buildTaskContentEditQuery(
  contentId: string,
  type: EditableContentType,
  contentPath: string,
  subContentType?: string
) {
  return {
    action: "edit",
    contentId: contentId,
    contentType: type,
    dataPath: contentPath,
    subContentType: subContentType,
  };
}

export function buildCalendarContentRescheduleQuery(
  eventId: string,
  action: "reschedule" = "reschedule"
) {
  return {
    action,
    eventId,
  };
}

export function extractTaskEditContentQuery(query: Record<string, any>) {
  const { action, contentId, contentType, dataPath, subContentType } = query;
  if (action !== "edit") return null;
  if (!contentId || !contentType || !dataPath) return null;
  if (typeof contentId !== "string") return null;
  if (
    typeof contentType !== "string" ||
    !editableContentTypeOptions.includes(contentType as EditableContentType)
  )
    return null;
  return {
    action,
    contentId,
    contentType: contentType as EditableContentType,
    dataPath,
    subContentType: typeof subContentType === "string" ? subContentType : undefined,
  };
}

export function extractCalendarContentRescheduleQuery(query: Record<string, any>) {
  if (!query || typeof query !== "object") return null;
  const { action, eventId } = query;
  if (action !== "reschedule") return null;
  if (!eventId || typeof eventId !== "string") return null;
  return {
    action,
    eventId,
  };
}

export function getIntegrationIdContentType(
  contentType: ScheduledContentType | null
): Extract<
  IntegrationTypes,
  "facebook" | "twitter" | "instagram" | "linkedin" | "wordpress" | "tiktok" | "youtube"
> | null {
  switch (contentType) {
    case "social-media/facebook/post-to-facebook":
      return "facebook";
    case "social-media/twitter/post-to-twitter":
      return "twitter";
    case "social-media/instagram/post-to-instagram":
      return "instagram";
    case "social-media/linkedin/post-to-linkedin":
      return "linkedin";
    case "social-media/wordpress/post-to-wordpress":
      return "wordpress";
    case "social-media/tiktok/post-to-tiktok":
      return "tiktok";
    case "social-media/youtube/post-to-youtube-channel-description":
    case "social-media/youtube/post-to-youtube-video":
      return "youtube";
    default:
      return null;
  }
}

export function extractScheduledTaskTypeFromContentType(contentType: string) {
  if (!contentType) return null;
  let platform = contentType.split("/")[1];
  if (platform === "article") platform = "wordpress";
  return getContentTypeByIntegrationId(platform as IntegrationTypes);
}

export function getContentTypeByIntegrationId(integrationId: IntegrationTypes) {
  switch (integrationId) {
    case "facebook":
      return "social-media/facebook/post-to-facebook";
    case "twitter":
      return "social-media/twitter/post-to-twitter";
    case "instagram":
      return "social-media/instagram/post-to-instagram";
    case "linkedin":
      return "social-media/linkedin/post-to-linkedin";
    case "tiktok":
      return "social-media/tiktok/post-to-tiktok";
    case "wordpress":
      return "social-media/wordpress/post-to-wordpress";
    case "youtube":
      return "social-media/youtube/post-to-youtube-video";
    default:
      return null;
  }
}

export function getScheduledContentTypeIcon(type: ScheduledContentType) {
  switch (type) {
    case "social-media/wordpress/post-to-wordpress":
      return defineAsyncComponent(() => import("@/components/icons/WordpressIcon.vue"));
    case "social-media/facebook/post-to-facebook":
      return getSocialIcon("facebook");
    case "social-media/instagram/post-to-instagram":
      return getSocialIcon("instagram");
    case "social-media/twitter/post-to-twitter":
      return getSocialIcon("twitter");
    case "social-media/linkedin/post-to-linkedin":
      return getSocialIcon("linkedin");
    case "social-media/tiktok/post-to-tiktok":
      return getSocialIcon("tiktok");
    case "social-media/youtube/post-to-youtube-channel-description":
    case "social-media/youtube/post-to-youtube-video":
      return getSocialIcon("youtube");
    default:
      return null;
  }
}

export function formatFbTwitterAndLinkedinContentForSelection(
  _content: IContentBase
): IContentSelectOption[] {
  const content = _content as IContentBase<
    WriteTwitterPostResult | WriteLinkedInPostResult
  >;

  const contentList: IContentSelectOption[] = [];

  if (!content) return contentList;
  const resultData = get(content, "resultData");
  if (!resultData) return contentList;

  // Extract videos from content
  let videos: string[] = [];
  if (content.videos) {
    if (typeof content.videos === "string") {
      videos = [content.videos];
    } else if (Array.isArray(content.videos)) {
      videos = content.videos;
    }
  }

  contentList.push({
    contentId: content.id,
    text: resultData.content,
    images: content.images || [],
    videos: videos.length > 0 ? videos : undefined,
    taskType: content.taskType,
  });
  return contentList;
}

export function formatInstagramContentForSelection(_content: IContentBase) {
  const content = _content as IContentBase<WriteInstagramPostResult>;
  const contentList: IContentSelectOption[] = [];
  if (!content) return contentList;
  const { resultData, images, id } = content;
  if (!resultData) return contentList;
  let img: string[] = [];
  if (images && images.length) img = images;
  else img = [resultData.imageUrl];

  // Extract videos from content
  let videos: string[] = [];
  if (content.videos) {
    if (typeof content.videos === "string") {
      videos = [content.videos];
    } else if (Array.isArray(content.videos)) {
      videos = content.videos;
    }
  }

  contentList.push({
    contentId: id,
    text: resultData.caption,
    images: img,
    videos: videos.length > 0 ? videos : undefined,
    taskType: content.taskType,
  });
  return contentList;
}

export function formatTikTokContentForSelection(
  _content: IContentBase
): IContentSelectOption[] {
  type TikTokPostResultData = {
    content: string;
    hashtags?: string[];
    mentions?: string[];
  };
  type TikTokResultData = string | TikTokPostResultData;
  const content = _content as unknown as IContentBase<TikTokResultData>;
  const contentList: IContentSelectOption[] = [];
  if (!content) return contentList;

  const resultData = get(content, "resultData") as TikTokResultData | undefined;
  if (!resultData) return contentList;

  let text = "";
  if (typeof resultData === "string") {
    text = resultData;
  } else if (typeof resultData === "object" && "content" in resultData) {
    const rd = resultData as TikTokPostResultData;
    const hashtags = Array.isArray(rd.hashtags) ? rd.hashtags.join(" ") : "";
    text = hashtags ? `${rd.content} ${hashtags}`.trim() : rd.content;
  }

  let videos: string[] = [];
  if (content.videos) {
    videos = Array.isArray(content.videos) ? content.videos : [content.videos];
  }

  contentList.push({
    contentId: content.id,
    text,
    images: content.images || [],
    videos: videos.length > 0 ? videos : undefined,
    taskType: content.taskType,
  });
  return contentList;
}

export function formatYouTubeContentForSelection(
  _content: IContentBase
): IContentSelectOption[] {
  const contentList: IContentSelectOption[] = [];
  if (!_content) return contentList;
  const resultData = get(_content, "resultData") as Record<string, any> | undefined;
  if (!resultData) return contentList;

  let videos: string[] = [];
  if (_content.videos) {
    videos = Array.isArray(_content.videos) ? _content.videos : [_content.videos];
  }

  const text = resultData.description || resultData.content || "";
  contentList.push({
    contentId: _content.id,
    text,
    images: _content.images || [],
    videos: videos.length > 0 ? videos : undefined,
    taskType: _content.taskType,
  });
  return contentList;
}

export function formatSocialPostForSelection(_content: IContentBase) {
  const content = _content as IContentBase<SocialPostResult>;
  const contentList: IContentSelectOption[] = [];
  if (!content) return contentList;
  if (content.taskType === "social-media/tiktok/write-post-captions") {
    return formatTikTokContentForSelection(content);
  }
  if (content.taskType.startsWith("social-media/youtube/")) {
    return formatYouTubeContentForSelection(content);
  }
  if (!content.taskType.endsWith("write-post")) return contentList;
  if (content.taskType === "social-media/instagram/write-post") {
    return formatInstagramContentForSelection(content);
  } else return formatFbTwitterAndLinkedinContentForSelection(content);
}

export function formatTaskResultForWordpress(content: IContentBase) {
  const data: IContentSelectOption = {
    contentId: content.id,
    text: "",
    images: [],
    resultIndex: 0,
    taskType: content.taskType,
  };
  if (!content) return data;
  try {
    switch (content.taskType) {
      case "copywriting/article/write-post":
        data.text = (content.resultData as WritePostResult)?.final || "";
        break;
      case "copywriting/article/research-write-post":
        data.text = (content.resultData as ResearchWritePostResult)?.finalArticle || "";
        break;
      case "content/combined/write-posts":
        data.text = (content.resultData as CombinedContentTaskResult)?.finalArticle || "";
    }
  } catch {
    return data;
  }

  return data;
}

export const contentTypeTaskCategoryMap: Record<ScheduledContentType, string> = {
  "social-media/facebook/post-to-facebook": "social-media/facebook",
  "social-media/facebook/announce-event": "social-media/facebook",
  "social-media/instagram/post-to-instagram": "social-media/instagram",
  "social-media/linkedin/post-to-linkedin": "social-media/linkedin",
  "social-media/twitter/post-to-twitter": "social-media/twitter",
  "social-media/tiktok/post-to-tiktok": "social-media/tiktok",
  "social-media/wordpress/post-to-wordpress": "copywriting/article/write-post",
  "social-media/youtube/post-to-youtube-channel-description": "social-media/youtube",
  "social-media/youtube/post-to-youtube-video": "social-media/youtube",
};
export const contentTypeTaskKeyMap: Record<ScheduledContentType, string> = {
  "social-media/facebook/post-to-facebook": "social-media/facebook/write-post",
  "social-media/facebook/announce-event": "social-media/facebook/announce-event",
  "social-media/instagram/post-to-instagram": "social-media/instagram/write-post",
  "social-media/linkedin/post-to-linkedin": "social-media/linkedin/write-post",
  "social-media/twitter/post-to-twitter": "social-media/twitter/write-post",
  "social-media/tiktok/post-to-tiktok": "social-media/tiktok/write-post-captions",
  "social-media/wordpress/post-to-wordpress": "copywriting/article/write-post",
  "social-media/youtube/post-to-youtube-channel-description":
    "social-media/youtube/write-channel-description",
  "social-media/youtube/post-to-youtube-video":
    "social-media/youtube/write-video-description",
};

export function buildPostIntegrationPayload<
  T extends ContentIntegrationType = ContentIntegrationType,
>(platform: T, integrationIds: string[], integrations: IUserIntegration[]) {
  const activeIntegrations = findIntegrations(integrations, platform, true).filter(
    integration => integrationIds.includes(integration.id)
  );

  if (!activeIntegrations.length) return [];

  switch (platform) {
    case "instagram":
      return activeIntegrations.map(integration => ({
        id: integration.id,
        instagramBusinessId: (integration.data as IInstagramData).pageId,
      }));
    case "facebook":
      return activeIntegrations.map(integration => ({
        id: integration.id,
        pageId: (integration.data as IFacebookData).pageId,
      }));
    case "twitter":
      return activeIntegrations.map(integration => ({
        id: integration.id,
        pageId: "na", // Twitter does not use pageId
      }));
    case "linkedin":
      return activeIntegrations.map(integration => ({
        id: integration.id,
        pageId: (integration.data as ILinkedInData).profile.name,
      }));
    case "tiktok":
      return activeIntegrations.map(integration => ({
        id: integration.id,
      }));
    case "youtube":
      return activeIntegrations.map(integration => ({
        id: integration.id,
        channelId: (integration.data as IYoutubeData).profile.id,
      }));
    case "wordpress":
      return [];
    default:
      return [];
  }
}

export function addLinkedInOrganizationsToPayload(
  integrationData: any[],
  linkedInIntegrations: IUserIntegration<"linkedin">[],
  selectedOrgIds: string[],
  enablePersonalProfile: boolean
): any[] {
  // Check if this is an "organization only" post
  const isOrgOnly = !enablePersonalProfile && selectedOrgIds.length > 0;

  // Filter integrations Id based on org
  const selectedIntegrations = linkedInIntegrations.filter(integration => {
    const orgIds = getIntegrationOrgIds(integration);

    return isOrgOnly
      ? selectedOrgIds.some(orgId => orgIds.includes(orgId.toString()))
      : integrationData.some(data => data.id === integration.id);
  });

  // Map selected integrations to the required format
  return selectedIntegrations.map(integration => {
    const orgIds = getIntegrationOrgIds(integration);
    let postOrgs: string[] = [];

    // Special case: Single account selected without orgs, but orgs are requested
    if (
      selectedIntegrations.length === 1 &&
      orgIds.length === 0 &&
      selectedOrgIds.length > 0
    ) {
      postOrgs = selectedOrgIds;
    } else {
      postOrgs = selectedOrgIds.filter(orgId => orgIds.includes(orgId.toString()));
    }

    return {
      id: integration.id,
      pageId:
        integration.data &&
        "profile" in integration.data &&
        integration.data.profile?.name
          ? integration.data.profile.name
          : "",
      postToOrganizations: postOrgs,
      postToPersonalProfile: enablePersonalProfile,
    };
  });
}

function getIntegrationOrgIds(integration: any): string[] {
  return (
    integration.data?.organizations?.map((o: { id: { toString: () => any } }) =>
      o.id.toString()
    ) ?? []
  );
}
